import React from 'react';
import PropTypes from 'prop-types';

import SkeletonBox from 'shared-components/components/SkeletonBox';
import SkeletonLine from 'shared-components/components/SkeletonLine';

import { getMainLocalityText, getButtonText } from '@helpers/localityDisplay';

import './critical.scss';

const propTypes = {
  open: PropTypes.bool,
  isPublisherPage: PropTypes.bool,
  isAgencySrp: PropTypes.bool,
  showPTypesButton: PropTypes.bool,
  showLicenceTypeGroupsButton: PropTypes.bool,
  showLicenceContractTypeGroupsButton: PropTypes.bool,
  hasPois: PropTypes.bool,
  locationHasChildren: PropTypes.bool,
  skBox1: PropTypes.number,
  skBox2: PropTypes.number,
  skBox3: PropTypes.number,
  skBox4: PropTypes.number,
  skBox5: PropTypes.number,
};

const defaultProps = {
  open: false,
  isPublisherPage: false,
  isAgencySrp: false,
  showPTypesButton: false,
  showLicenceTypeGroupsButton: false,
  showLicenceContractTypeGroupsButton: false,
  hasPois: true,
  locationHasChildren: true,  
  skBox1: 0,
  skBox2: 0,
  skBox3: 0,
  skBox4: 0,
  skBox5: 0,
};

const mainLocationRow = ({
  locationHasChildren,
  isCircleorNearby,
  mainLocation,
  qExpansion,
  isAgencySrp = false,
}) => (
  <div className={`skl__ml${locationHasChildren || isAgencySrp ? ' with-children' : ''}${isCircleorNearby ? ' is-circle-nearby' : ''}`}>
    <span className="skl__ghost is-rel">
      <span className="skl__ghost__hid">
        {!isCircleorNearby && getMainLocalityText({
          mainLoc: mainLocation,
          qExpansion,
          hasChildrenLocality: locationHasChildren,
          isRadiusSearch: false, // filters?.geocircle,
        })}
      </span>
      <SkeletonLine
        width="100%"
        className="skl__ghost__line"
      />            
    </span>
  </div>
);

const childrenLocationsRow = ({
  mainLocation,
  additionalLocations,
  qExpansion,
  isAgencySrp = false,
}) => {
  return (
    <div className={`skl__ml${isAgencySrp ? ' with-children' : ''}`}>
      <span className="skl__ghost is-rel">
        <span className="skl__ghost__hid">
          {getButtonText({
            mainLevel: mainLocation.level,
            children: additionalLocations,
            qExpansion,
          })}
        </span>
        <SkeletonLine
          width="100%"
          className="skl__ghost__line"
        />
      </span>
    </div>
  );
};

const Skeleton = ({
  isPublisherPage,
  isAgencySrp,
  mainLocation,
  additionalLocations,  
  qExpansion,  
  isCircleorNearby,
  activeFilters,     
  showPTypesButton,
  showLicenceTypeGroupsButton,
  // showLicenceContractTypeGroupsButton,
  hasPois,
  isPoiActive,
  skBox1,
  skBox2,
  skBox3,
  skBox4,
  skBox5,
}) => {
  const locationHasChildren = additionalLocations && additionalLocations.length > 0;

  if (isAgencySrp) {
    return (
      <div className="is-vis-from-s">
        <SkeletonBox
          className="c-bg--w pr--ms pl--ms"
          mb="10"
          width="100%"
          boxed
        >
          {/* type */}
          <SkeletonLine
            mt="10"
            mb="10"
            width="100%"
            height="19"
          />
          {isPublisherPage && (
            <SkeletonLine
              mt="10"
              mb="10"
              width="100%"
              height="19"
            />          
          )}
          {/* channel */}
          <SkeletonLine
            mt="11"
            mb="11"
            width="100%"
            height="21"
          />
          {/* main location */}
          {mainLocationRow({ locationHasChildren, isCircleorNearby, mainLocation, qExpansion, isAgencySrp })}
          {/* additional locations */}
          {locationHasChildren && childrenLocationsRow({ mainLocation, additionalLocations, qExpansion, isAgencySrp })}
          <SkeletonLine
            mt="10"
            mb="10"
            width="100%"
            height="18"
          />          
        </SkeletonBox>      
      </div>
    );
  }

  const box5 = isPublisherPage ? skBox5 - 1 : skBox5; // in publisher page the first box is the one under the property type, so we need to remove it from the count of the "other filters" boxes

  return (
    <div className="is-vis-from-s">
      <SkeletonBox
        className="c-bg--w pr--ms pl--ms"
        mb="10"
        width="100%"
        boxed
      >
        {/* type */}
        <SkeletonLine
          mt="10"
          mb="10"
          width="100%"
          height="19"
        />
        {isPublisherPage && (
          <SkeletonLine
            mt="10"
            mb="10"
            width="100%"
            height="19"
          />          
        )}
        {/* channel */}
        <SkeletonLine
          mt="11"
          mb="11"
          width="100%"
          height="21"
        />
        {/* main location */}
        {mainLocationRow({ locationHasChildren, isCircleorNearby, mainLocation, qExpansion })}
        {/* additional locations */}
        {locationHasChildren && childrenLocationsRow({ mainLocation, additionalLocations, qExpansion })}
      </SkeletonBox>
      {showPTypesButton && (
        <SkeletonBox
          className="c-bg--w pr--ms pl--ms"
          mb={showLicenceTypeGroupsButton ? 10 : 16}
          width="100%"
          boxed
        >
          <SkeletonLine
            mt="10"
            mb="10"
            width="100%"
            height="19"
          />          
        </SkeletonBox>
      )}
      {showLicenceTypeGroupsButton && (
        <SkeletonBox
          className="c-bg--w pr--ms pl--ms"
          mb="10"
          width="100%"
          boxed
        >
          <SkeletonLine
            mt="10"
            mb="10"
            width="100%"
            height="19"
          />
        </SkeletonBox>        
      )}
      {activeFilters > 0 ? (
        <>
          {/** titolo filtri attivi */}
          <div className="pt--m pb--m">
            <SkeletonLine ml="16" width="80" height="18" />
          </div>
          {/** poi se attivo */}
          {isPoiActive && hasPois ? (
            <SkeletonBox
              className="c-bg--w pr--ms pl--ms"
              mb="16"
              width="100%"
              boxed
            >
              <SkeletonLine
                mt="11"
                mb="11"
                width="100%"
                height="20"
              />
            </SkeletonBox>
          ) : null}
          {/** box filtri attivi */}
          <SkeletonBox
            className="c-bg--w pr--ms pl--ms"
            mb="16"
            width="100%"
            boxed
          >
            {Array.from({ length: activeFilters - (isPoiActive ? 1 : 0) }).map((_, i, arr) => (
              <SkeletonLine
                key={i}
                mt={i === arr.length - 1 ? "11" : "12"}
                mb="11"
                width="100%"
                height="20"
              />
            ))}
          </SkeletonBox>
          {/** titolo altri filtri */}
          <div className="pb--m">
            <SkeletonLine ml="16" width="80" height="18" />
          </div>
          {hasPois && (
            <SkeletonBox
              className="c-bg--w pr--ms pl--ms"
              mb="16"
              width="100%"
              boxed
            >
              <SkeletonLine
                mt="11"
                mb="11"
                width="100%"
                height="20"
              />
            </SkeletonBox>
          )}          
        </>
      ) : (
        <>
          <div className="pt--m pb--m">
            <SkeletonLine ml="16" width="80" height="18" />
          </div>      
          {hasPois && (
            <SkeletonBox
              className="c-bg--w pr--ms pl--ms"
              mb="16"
              width="100%"
              boxed
            >
              <SkeletonLine
                mt="11"
                mb="11"
                width="100%"
                height="20"
              />
            </SkeletonBox>
          )}
        </>
      )}
      {skBox1 > 0 && (
        <SkeletonBox className="c-bg--w pr--m pl--m" width="100%" mb="16" boxed>
          {[...Array(skBox1).keys()].map((a, i) => (
            <SkeletonLine
              key={`bot_${i + 1}`}
              mt={i === skBox1 - 1 ? '11' : '12'}
              mb="11"
              width="100%"
              height="20"
            />
          ))}
        </SkeletonBox>
      )}      
      {skBox2 > 0 && (
        <SkeletonBox className="c-bg--w pr--m pl--m" width="100%" mb="16" boxed>
          {[...Array(skBox2).keys()].map((a, i) => (
            <SkeletonLine
              key={`bot_${i + 1}`}
              mt={i === skBox2 - 1 ? '11' : '12'}
              mb="11"
              width="100%"
              height="20"
            />
          ))}
        </SkeletonBox>
      )}
      {skBox3 > 0 && (
        <SkeletonBox className="c-bg--w pr--m pl--m" width="100%" mb="16" boxed>
          {[...Array(skBox3).keys()].map((a, i) => (
            <SkeletonLine
              key={`bot_${i + 1}`}
              mt={i === skBox3 - 1 ? '11' : '12'}
              mb="11"
              width="100%"
              height="20"
            />
          ))}
        </SkeletonBox>
      )}
      {skBox4 > 0 && (
        <SkeletonBox className="c-bg--w pr--m pl--m" width="100%" mb="16" boxed>
          {[...Array(skBox4).keys()].map((a, i) => (
            <SkeletonLine
              key={`bot_${i + 1}`}
              mt={i === skBox4 - 1 ? '11' : '12'}
              mb="11"
              width="100%"
              height="20"
            />
          ))}
        </SkeletonBox>
      )}
      {box5 > 0 && (
        <SkeletonBox className="c-bg--w pr--m pl--m" width="100%" boxed>
          {[...Array(box5).keys()].map((a, i) => (
            <SkeletonLine
              key={`bot_${i + 1}`}
              mt={i === box5 - 1 ? '11' : '12'}
              mb="11"
              width="100%"
              height="20"
            />
          ))}
        </SkeletonBox>
      )}      
    </div>
  );
};

Skeleton.propTypes = propTypes;
Skeleton.defaultProps = defaultProps;
export default Skeleton;
